import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tags, Info } from "lucide-react";
import { Profile } from "@/hooks/useProfile";

interface ProfileTagsProps {
  profile: Profile;
  onTagsUpdate: (tags: string[]) => void;
}

const AVAILABLE_TAGS = [
  "Seed Alternative",
  "Open to Partner", 
  "Coop Member"
];

export const ProfileTags = ({ profile, onTagsUpdate }: ProfileTagsProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const currentTags = profile.profile_tags || [];

  const handleTagToggle = (tag: string, checked: boolean) => {
    const updatedTags = checked 
      ? [...currentTags, tag]
      : currentTags.filter(t => t !== tag);
    
    onTagsUpdate(updatedTags);
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset to original tags if needed
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base font-medium">
          <div className="flex items-center gap-2">
            <Tags className="w-4 h-4" />
            Profile Tags
            <Info className="w-4 h-4 text-muted-foreground" />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? "Cancel" : "Edit"}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEditing ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select tags that describe your farm and interests:
            </p>
            <div className="space-y-3">
              {AVAILABLE_TAGS.map((tag) => (
                <div key={tag} className="flex items-center space-x-2">
                  <Checkbox
                    id={tag}
                    checked={currentTags.includes(tag)}
                    onCheckedChange={(checked) => handleTagToggle(tag, checked as boolean)}
                  />
                  <label
                    htmlFor={tag}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {tag}
                  </label>
                </div>
              ))}
            </div>
            <Button onClick={handleSave} size="sm">
              Save Tags
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {currentTags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {currentTags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No tags selected. Click Edit to add profile tags.
              </p>
            )}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Info:</strong> Profile tags help other farmers discover your interests and specialties. They will be visible on your public profile.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};